import { Box, Button, Grid, Paper, Typography, useMediaQuery } from "@material-ui/core"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import { CloudUpload, Delete, Image } from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import React, { useEffect, useRef, useState } from "react"
import { BotaoVoltar } from "./BotaoVoltar"
import { useArquivosAplicativoContext } from "../context/useArquivosAplicativoContext"
import type { CampoArquivo } from "../models/arquivosAplicativoModel"

const CAMPO_LOGO: CampoArquivo = {
    campo: "urlLogo",
    label: "Logo",
    tamanho: "192x192 (não pode ser redonda)",
}

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: {
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "80px",
        },
        [theme.breakpoints.down("sm")]: {
            paddingLeft: "15px",
            paddingRight: "15px",
            paddingTop: "70px",
        },
    },
    content: {
        margin: "auto",
        maxWidth: "1100px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    header: {
        marginBottom: theme.spacing(4),
        [theme.breakpoints.down("sm")]: { marginBottom: theme.spacing(3) },
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: {
            borderRadius: 15,
            marginBottom: theme.spacing(2),
        },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    imageCard: {
        borderRadius: 12,
        border: "1px solid #e9ecef",
        padding: theme.spacing(2),
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1),
        height: "100%",
    },
    imagePreview: {
        width: "100%",
        height: 100,
        backgroundColor: "#f8f9fa",
        borderRadius: 8,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        border: "1px dashed #dee2e6",
    },
    previewImg: {
        maxWidth: "100%",
        maxHeight: "100%",
        objectFit: "contain",
    },
    noImageIcon: {
        color: "#adb5bd",
        fontSize: 40,
    },
    fieldLabel: {
        fontWeight: 600,
        color: "#2c3e50",
        fontSize: "0.85rem",
        lineHeight: 1.3,
    },
    fieldSize: {
        color: "#7f8c8d",
        fontSize: "0.75rem",
    },
    actions: {
        display: "flex",
        gap: theme.spacing(1),
        marginTop: "auto",
    },
    uploadBtn: {
        borderRadius: 20,
        fontSize: "0.75rem",
        textTransform: "none",
        backgroundColor: "var(--cor-botao-salvar)",
        color: "white",
        "&:hover": { backgroundColor: "var(--cor-botao-salvar-hover)" },
        flex: 1,
    },
    removeBtn: {
        borderRadius: 20,
        fontSize: "0.75rem",
        textTransform: "none",
        color: "var(--cor-erro)",
        borderColor: "var(--cor-erro)",
    },
}))

type ImageCardProps = {
    campo: CampoArquivo
    url: string | null
    disabled: boolean
    onUpload: (campo: string, file: File) => Promise<void>
    onRemover: (campo: string) => Promise<void>
}

function ImageCard({ campo, url, disabled, onUpload, onRemover }: ImageCardProps) {
    const classes = useStyles()
    const { enqueueSnackbar } = useSnackbar()
    const [uploading, setUploading] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        if (inputRef.current) inputRef.current.value = ""
        setUploading(true)
        try {
            await onUpload(campo.campo, file)
            enqueueSnackbar("Imagem enviada com sucesso!", { variant: "success" })
        } catch (err) {
            enqueueSnackbar((err as Error).message || "Erro ao enviar imagem", { variant: "error" })
        } finally {
            setUploading(false)
        }
    }

    const handleRemover = async () => {
        setUploading(true)
        try {
            await onRemover(campo.campo)
            enqueueSnackbar("Imagem removida.", { variant: "success" })
        } catch (err) {
            enqueueSnackbar((err as Error).message || "Erro ao remover imagem", { variant: "error" })
        } finally {
            setUploading(false)
        }
    }

    const isFirebase = campo.campo === "urlArquivoFireBase"
    const acceptTypes = campo.aceitarTipos ?? "image/*,.jpg,.jpeg,.png,.gif"

    return (
        <Box className={classes.imageCard}>
            <Typography className={classes.fieldLabel}>{campo.label}</Typography>
            {campo.tamanho && (
                <Typography className={classes.fieldSize}>{campo.tamanho}</Typography>
            )}
            <Box className={classes.imagePreview}>
                {url ? (
                    isFirebase ? (
                        <Typography variant="caption" style={{ color: "#7f8c8d", padding: 4, wordBreak: "break-all" }}>
                            {url.split("/").pop()}
                        </Typography>
                    ) : (
                        <img src={url} alt={campo.label} className={classes.previewImg} />
                    )
                ) : (
                    <Image className={classes.noImageIcon} />
                )}
            </Box>
            <Box className={classes.actions}>
                <input
                    ref={inputRef}
                    type="file"
                    accept={acceptTypes}
                    style={{ display: "none" }}
                    id={`upload-input-${campo.campo}`}
                    onChange={handleFileChange}
                    disabled={disabled || uploading}
                />
                <label htmlFor={`upload-input-${campo.campo}`} style={{ flex: 1, display: "flex" }}>
                    <Button
                        component="span"
                        variant="contained"
                        size="small"
                        className={classes.uploadBtn}
                        disabled={disabled || uploading}
                        startIcon={<CloudUpload style={{ fontSize: 14 }} />}
                        fullWidth
                    >
                        {uploading ? "Enviando..." : "Enviar"}
                    </Button>
                </label>
                {url && (
                    <Button
                        variant="outlined"
                        size="small"
                        className={classes.removeBtn}
                        onClick={handleRemover}
                        disabled={disabled || uploading}
                        style={{ minWidth: 0, padding: "4px 8px" }}
                    >
                        <Delete style={{ fontSize: 14 }} />
                    </Button>
                )}
            </Box>
        </Box>
    )
}

export function FormularioArquivosAplicativo() {
    const classes = useStyles()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
    const { arquivos, erroCarregamento, uploadArquivo, removerArquivo } = useArquivosAplicativoContext()
    const { enqueueSnackbar } = useSnackbar()

    const somenteLeitura = arquivos.podeEditar === false
    const desabilitado = !!erroCarregamento || somenteLeitura

    useEffect(() => {
        if (erroCarregamento) enqueueSnackbar(erroCarregamento, { variant: "error" })
    }, [erroCarregamento, enqueueSnackbar])

    // A logo enviada aqui preenche tanto a logo quanto a logo grande do aplicativo.
    const enviarLogo = async (_campo: string, file: File) => {
        await uploadArquivo("urlLogo", file)
        await uploadArquivo("urlImgAindaNaoTemSelo", file)
    }

    const removerLogo = async (_campo: string) => {
        await removerArquivo("urlLogo")
        await removerArquivo("urlImgAindaNaoTemSelo")
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <BotaoVoltar />
                <Box className={classes.header}>
                    <Typography variant={isMobile ? "h5" : "h4"} className={classes.titleText}>
                        Arquivos do Aplicativo
                    </Typography>
                    {somenteLeitura && (
                        <Typography variant="body2" style={{ color: "#e67e22", marginTop: 4 }}>
                            Este estabelecimento não possui aplicativo próprio. Os arquivos são somente leitura.
                        </Typography>
                    )}
                </Box>

                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Image className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Logo
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={3}>
                                <ImageCard
                                    campo={CAMPO_LOGO}
                                    url={arquivos.urlLogo as string | null}
                                    disabled={desabilitado}
                                    onUpload={enviarLogo}
                                    onRemover={removerLogo}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>
            </Box>
        </Box>
    )
}
